/**
 * iOS App Clip — the trial rules. See specs/app-clip.md.
 *
 * An App Clip is the full game, installed on demand from a link or a Safari card, never bought.
 * It loads the SAME deployed bundle as the paid app from the same URL, so every language and
 * every lesson is already there at no extra size — which is the whole point. What the clip adds
 * is a cap: each lesson plays `CLIP_BOARDS_PER_LESSON` boards, then forwards to the paid app.
 *
 * The cap is deliberately PER LESSON, not per session. A parent judging a reading game needs to
 * see that it is more than one drill — a few boards of Daily Life, then a few more of the gap
 * lesson in another language if they want them. "Try another lesson" is the loop that shows the
 * breadth; capping the whole trial instead would hide everything past the first lesson.
 *
 * The same rules also drive the **web demo**: `?demo` puts an ordinary browser into this capped
 * flow on any host, production included, so a campaign or a review link can show the trial and end
 * on the sale. `isClipMode()` is the single gate for both. Without that flag the web build, the
 * Android TWA and the paid iOS app all take the same branches they always did.
 */
import { platform } from '@/platform';
import { devFlag, urlFlag } from '@/game/dev';
import { storeTarget } from '@/game/storeTarget';

/**
 * Boards a single lesson may play before the clip asks for the sale.
 *
 * **Five, raised from three on 2026-09-08 after playing it on a phone.** Three boards is under a
 * minute, which was chosen to leave the parent curious — but a minute turns out to be less than a
 * child needs to *start* reading rather than to work out what the controls do, and a trial that
 * ends before anyone is enjoying it demonstrates nothing worth paying for. Five still ends while
 * they are ahead, and it now reaches a second word set, so the journey bar visibly moves.
 *
 * It is the same number as `DEMO_BOARDS_PER_LESSON` today and still a separate constant — see the
 * note there. The two answer to different pressures and are free to diverge again; nothing reads
 * one for the other, and `clip-trial.test.ts` asserts which constant is in force rather than which
 * literal, so they cannot quietly collapse into one.
 */
export const CLIP_BOARDS_PER_LESSON = 5;

/**
 * The **web demo's** allowance, deliberately its own constant and its own number.
 *
 * The two answer to different pressures. The clip's three is bounded by what Apple's on-demand
 * install is for: a taste, then the sale, with the paid app one tap away the whole time. The demo
 * is a campaign landing page — the visitor arrived from an ad, owes us nothing, and leaving costs
 * them a swipe — so it has to earn the pitch before it makes it. Five boards is long enough to
 * reach a second word set and see the journey bar move, which is what makes the card at the end
 * read as an offer rather than a toll gate.
 *
 * Move either freely; nothing reads one for the other (see boardsPerLesson()). They happen to be
 * equal since the clip's allowance was raised to five, which is a coincidence of two arguments
 * landing on the same number and not a reason to merge them.
 */
export const DEMO_BOARDS_PER_LESSON = 5;

/**
 * The App Clip's rules, either for real or under review.
 *
 * `?clip` on a dev or staging host is how the CLIP's flow gets looked at in an ordinary browser —
 * the clip runs in a native shell nobody can open on a desktop, so without this its cap is only
 * ever exercised by tests. It is devFlag(), so it cannot reach production, and it is deliberately
 * NOT the same switch as `?demo`: one reviews what iPhone users will get, the other IS what web
 * visitors get, and they no longer have the same allowance.
 */
export function isClipRules(): boolean {
  return platform().platform === 'ios-clip' || devFlag('clip');
}

/**
 * The public web demo — `?demo` in a real browser.
 *
 * No longer the only measured population: since 29 August 2026 every browser session is, and this
 * marks the advertising subset of it (`demo` on each event, and whether an ad tag may load). See
 * the header of src/analytics/index.ts.
 */
export function isWebDemo(): boolean {
  return platform().platform === 'web' && urlFlag('demo');
}

/**
 * The demo's other ending — the one for a visitor whose store we know.
 *
 * A board cap is what you use when you cannot make an offer: it stops the game and shows two
 * badges, because a browser could be any device. When the device names itself (an iPhone, an
 * Android phone, or a campaign that said so outright — see storeTarget.ts) there is a better
 * ending available, so the demo takes it: no board cap at all, play until the run reaches
 * **Saturn**, and end on that one moment with one button to the right store.
 *
 * Why Saturn and not a count: it is the first rung of the ladder that pays the player something
 * they can see themselves earning — the 💎 Diamond at `DIAMOND_AT_STACK_STARS` — so the ask lands
 * on a reward rather than on a wall. Five boards ends mid-sentence; Saturn ends on the best thing
 * that has happened yet, which is where a purchase is worth asking about.
 *
 * A demo game reaching Saturn takes 750 points — 75 correct smashes, well past the ~200 points
 * `DEMO_BOARDS_PER_LESSON` allowed (specs/analytics-ads.md). This is deliberately a longer, freer
 * trial for the visitor we can actually sell to, and the SAME free web game for everyone else.
 */
export function demoRunsToSaturn(): boolean {
  return isWebDemo() && storeTarget() !== null;
}

/**
 * Whichever allowance applies to the trial actually running.
 *
 * `Infinity` is not a lack of a rule: it is the Saturn run above, where the ending is a score and
 * not a board count. Everything that reads this compares rather than prints — the one place that
 * would have printed it (the HUD's `2/5`) asks `Number.isFinite` first, because "board 3 of ∞" is
 * worse than no label at all.
 */
export function boardsPerLesson(): number {
  if (isClipRules()) return CLIP_BOARDS_PER_LESSON;
  return demoRunsToSaturn() ? Infinity : DEMO_BOARDS_PER_LESSON;
}

/** { [packKey]: boards played }. packKey is `<packId>__<lang>` — see src/data/packs/index.ts. */
const BOARDS_KEY = 'abcs.clip.boards.v1';

/**
 * Whether the trial rules apply.
 *
 * Three sources:
 *  - the clip shell injects `window.__ABCS_PLATFORM__ = 'ios-clip'` before any page JS runs, so
 *    this is already true on the first frame (see src/platform/index.ts);
 *  - `?demo` in a BROWSER, on any host including production — the web demo. This is a real entry
 *    point, not a dev affordance: it is the link a campaign or an App Review note can send someone
 *    to, and it ends on the store card (src/game/ui/clipOutro.ts);
 *  - `?clip` on a dev or staging host, which reviews the CLIP's rules in a browser (see
 *    isClipRules) and never reaches production.
 *
 * The demo arm goes through isWebDemo(), which also requires platform `web`, so no URL can put
 * someone who BOUGHT the app into a capped game — `?demo` reaching a paid app's WebView by any
 * route is inert there. A flag has to be asked for either way, and a bare abcshooter.web.app visit
 * is untouched, so the free web build stays the complete game for anyone who just shows up.
 */
export function isClipMode(): boolean {
  return isClipRules() || isWebDemo();
}

function readCounts(): Record<string, number> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(BOARDS_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, number> = {};
    for (const [key, n] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof n === 'number' && Number.isFinite(n) && n > 0) out[key] = Math.floor(n);
    }
    return out;
  } catch {
    return {};   // corrupt or blocked storage: a fresh trial beats a broken one
  }
}

function writeCounts(counts: Record<string, number>): void {
  try {
    localStorage.setItem(BOARDS_KEY, JSON.stringify(counts));
  } catch { /* storage blocked — the cap simply doesn't persist, which errs toward generosity */ }
}

/** Boards this lesson has already played in the trial. */
export function clipBoardsPlayed(packKey: string): number {
  return readCounts()[packKey] ?? 0;
}

/** Boards this lesson has left, floored at 0. */
export function clipBoardsLeft(packKey: string): number {
  return Math.max(0, boardsPerLesson() - clipBoardsPlayed(packKey));
}

/** True once this lesson is used up — the point where the clip asks for the sale. */
export function isClipLessonSpent(packKey: string): boolean {
  return clipBoardsLeft(packKey) === 0;
}

/**
 * Count one completed board against this lesson's allowance. Returns the boards left
 * afterwards, so the caller can decide between "next board" and "the upsell" from one call.
 *
 * Counts on COMPLETION, not on start: a board abandoned by backing out to the menu, or one the
 * player lost their last life on, must not silently eat part of the trial.
 */
export function recordClipBoard(packKey: string): number {
  const cap = boardsPerLesson();
  const counts = readCounts();
  counts[packKey] = Math.min(cap, (counts[packKey] ?? 0) + 1);
  writeCounts(counts);
  return Math.max(0, cap - counts[packKey]);
}

/** Wipe the trial counters. Test/dev affordance — nothing in the app calls this. */
export function resetClipTrial(): void {
  try {
    localStorage.removeItem(BOARDS_KEY);
  } catch { /* nothing to do */ }
}
